import type { CatalogView, EmployeeView, ServiceView } from "../../../api/directory.api"
import { countOf as plural, minutesLabel } from "../../../lib/plural"
import { formatMinutes } from "../format-money"

/**
 * Каталог услуг 3-Б1 — категории, услуги и то, что о них надо знать с одного взгляда.
 *
 * ГЛАВНОЕ ЧИСЛО ЭКРАНА — «БЕЗ ИСПОЛНИТЕЛЕЙ». Услуга, которую никто не делает, стоит
 * в прайсе, но записаться на неё нельзя: онлайн-запись отдаёт пустой календарь, а
 * администратор по телефону упирается в «нет свободного времени» и не понимает, почему.
 * Поэтому «никто» — не серая подпись рядом с шестью мастерами, а отдельный цвет,
 * отдельный чип в фильтрах и отдельное слагаемое в счётчике шапки.
 *
 * ПАУЗА ВНУТРИ УСЛУГИ — тоже смысл, а не длительность. «30 + 40 пауза + 30» означает,
 * что в сорок минут выдержки мастер берёт другого клиента, а кресло остаётся занятым.
 * Свернуть это в «100 мин» — значит потерять час работы мастера в день.
 */

export interface CatalogServiceRow {
  readonly service: ServiceView
  /** «40 мин + буферы 5/5», «30 + 40 пауза + 30». */
  readonly durationLabel: string
  /** «6 мастеров» или «никто». */
  readonly performersLabel: string
  readonly performersCount: number
  readonly hasPause: boolean
  /** «онлайн» / «только админ». */
  readonly onlineLabel: string
}

export interface CatalogCategoryRow {
  readonly id: string
  readonly name: string
  /** «6 услуг». */
  readonly countLabel: string
  readonly services: readonly CatalogServiceRow[]
}

export interface CatalogModel {
  readonly categories: readonly CatalogCategoryRow[]
  /** «13 категорий · 200 услуг · 2 без исполнителей». Пусто, когда услуг нет вовсе. */
  readonly countLabel: string
  readonly withoutPerformers: number
  readonly totalServices: number
}

export type CatalogFilter = "active" | "all" | "orphan"

/**
 * Подпись длительности.
 *
 * Буферы показываются ОТДЕЛЬНО от длительности, а не складываются с ней: клиент видит
 * «40 минут», расписание мастера занимает 50. Сложить их в одно число — значит соврать
 * либо клиенту, либо мастеру, и выбирать между этими двумя вариантами не нужно.
 */
export function durationLabel(service: ServiceView): string {
  const pauseSteps = service.steps.filter((step) => !step.occupiesEmployee)
  if (service.steps.length > 1 && pauseSteps.length > 0) {
    return service.steps
      .map((step) => (step.occupiesEmployee ? `${step.durationMinutes}` : `${step.durationMinutes} пауза`))
      .join(" + ")
  }

  const base = formatMinutes(service.durationMinutes)
  if (service.bufferBeforeMinutes === 0 && service.bufferAfterMinutes === 0) return base
  return `${base} + буферы ${service.bufferBeforeMinutes}/${service.bufferAfterMinutes}`
}

/** «6 мастеров» / «4 мастера» / «никто». Слово «никто» — из макета и намеренно резкое. */
export function performersLabel(count: number): string {
  return count === 0 ? "никто" : plural(count, "мастер", "мастера", "мастеров")
}

export function buildCatalog(
  catalog: CatalogView,
  employees: readonly EmployeeView[],
  options: { readonly filter: CatalogFilter; readonly search: string } = {
    filter: "active",
    search: "",
  },
): CatalogModel {
  // Сколько мастеров делает услугу. Считаем по действующим: уволенный мастер
  // в числе исполнителей — это «услуга есть», хотя записать на неё уже некого.
  const performers = new Map<string, number>()
  for (const employee of employees) {
    if (employee.archivedAt !== null || !employee.isBookable) continue
    for (const serviceId of employee.serviceIds) {
      performers.set(serviceId, (performers.get(serviceId) ?? 0) + 1)
    }
  }

  const needle = options.search.trim().toLowerCase()

  const rows = catalog.services.map((service): CatalogServiceRow => {
    const count = performers.get(service.id) ?? 0
    return {
      service,
      durationLabel: durationLabel(service),
      performersLabel: performersLabel(count),
      performersCount: count,
      hasPause: service.steps.some((step) => !step.occupiesEmployee),
      onlineLabel: service.isOnlineBookable ? "онлайн" : "только админ",
    }
  })

  // Счётчик шапки считается по ВСЕМУ каталогу, а не по отфильтрованному виду:
  // «2 без исполнителей» обязано оставаться на месте, когда включён фильтр «Активные»,
  // иначе проблема исчезает с экрана ровно тогда, когда её пытаются найти.
  const withoutPerformers = rows.filter(
    (row) => row.service.isActive && row.performersCount === 0,
  ).length

  const visible = rows.filter((row) => {
    if (options.filter === "active" && !row.service.isActive) return false
    if (options.filter === "orphan" && (row.performersCount > 0 || !row.service.isActive)) return false
    if (needle.length > 0 && !row.service.name.toLowerCase().includes(needle)) return false
    return true
  })

  const byCategory = new Map<string, CatalogServiceRow[]>()
  for (const row of visible) {
    const key = row.service.categoryId ?? ""
    const list = byCategory.get(key) ?? []
    list.push(row)
    byCategory.set(key, list)
  }

  const categories: CatalogCategoryRow[] = []
  for (const category of catalog.categories) {
    const services = byCategory.get(category.id) ?? []
    if (services.length === 0) continue
    categories.push({
      id: category.id,
      name: category.name,
      countLabel: plural(services.length, "услуга", "услуги", "услуг"),
      services,
    })
  }

  // Услуги без категории показываются последними и своей строкой: спрятать их — значит
  // потерять из прайса то, что в нём есть.
  const uncategorized = byCategory.get("") ?? []
  if (uncategorized.length > 0) {
    categories.push({
      id: "",
      name: "Без категории",
      countLabel: plural(uncategorized.length, "услуга", "услуги", "услуг"),
      services: uncategorized,
    })
  }

  const totalServices = catalog.services.length
  const countLabel =
    totalServices === 0
      ? ""
      : [
          plural(catalog.categories.length, "категория", "категории", "категорий"),
          plural(totalServices, "услуга", "услуги", "услуг"),
          withoutPerformers > 0 ? `${withoutPerformers} без исполнителей` : null,
        ]
          .filter((part): part is string => part !== null)
          .join(" · ")

  return { categories, countLabel, withoutPerformers, totalServices }
}

// ───────────────────────── тексты пустого первого дня ─────────────────────────

export const CATALOG_EMPTY = {
  title: "Прайса пока нет",
  text: "С него начинается всё остальное: без услуг нельзя ни записать клиента, ни открыть онлайн-запись.",
  note: "Готовый набор — 24 услуги в 4 категориях, дальше правится. Два часа ввода превращаются в двадцать минут правки.",
  actions: [
    "Начать с готового набора — барбершоп",
    "Перенести прайс из прежней программы",
    "Добавить услугу вручную",
  ],
} as const

/** Подсказка карточки услуги про буферы. Числа подставляются, слова — из макета. */
export function bufferHint(service: ServiceView): string {
  const total = service.durationMinutes + service.bufferBeforeMinutes + service.bufferAfterMinutes
  return `Клиент увидит «${formatMinutes(service.durationMinutes)}». В расписании мастера услуга займёт ${formatMinutes(total)}: ${minutesLabel(service.bufferBeforeMinutes)} до и ${String(service.bufferAfterMinutes)} после.`
}

export const PRICE_HINT =
  "У отдельных мастеров цена может отличаться (Б6). Новая цена — для будущих записей, созданные сохранят свою."

export const RESOURCE_HINT =
  "Если свободного кресла нет, записать нельзя, даже когда мастер свободен."
